import {
  DEFAULT_RESPONSE_OUTPUT_TOKENS,
  RESPONSE_MODELS,
  resolveResponseModel,
  type AssistantConfiguration,
  type ConfigurationDraft,
} from '@cryptology-assist/domain';
import type { Copy } from './copy';

// A published configuration or a saved draft of one.
export type Settings = AssistantConfiguration | ConfigurationDraft;

// The sections of the editor that hold settings, in the order it shows them.
export const SETTINGS_SECTIONS = [
  'business',
  'information',
  'voice',
  'model',
  'appointments',
  'channels',
] as const;
export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];

function model(settings: Settings) {
  const chosen = resolveResponseModel(settings.responseModel);
  return {
    model: chosen.model,
    reasoningEffort: chosen.reasoningEffort,
    processingMode: chosen.processingMode ?? 'standard',
    maxOutputTokens: chosen.maxOutputTokens ?? DEFAULT_RESPONSE_OUTPUT_TOKENS,
  };
}

function outcomes(settings: Settings): string[] {
  if (settings.commercialOutcomes) return [...settings.commercialOutcomes];
  if (!settings.capabilities.includes('sales')) return [];
  return [
    'qualified_lead',
    ...(settings.calendar || settings.appointmentServices?.length
      ? ['appointment']
      : []),
  ];
}

// Each section as the assistant reads it, so that a setting written out equals
// the same setting left to its default.
const sections: Record<SettingsSection, (settings: Settings) => unknown> = {
  business: (settings) => ({
    assistantName: settings.profile.assistantName,
    businessName: settings.profile.businessName,
    locale: settings.profile.locale,
    formality: settings.profile.formality,
    business: settings.business ?? null,
    capabilities: [...settings.capabilities].sort(),
    outcomes: outcomes(settings).sort(),
    leadDestination: settings.leadDestination ?? 'business',
  }),
  information: (settings) => ({
    connection: settings.businessConnectionId,
    catalog: settings.catalog ?? null,
  }),
  voice: (settings) => ({
    personality: settings.profile.personality ?? null,
    instructions: settings.profile.instructions,
    signature: settings.profile.signature,
    playbook: settings.salesPlaybook ?? null,
  }),
  model,
  appointments: (settings) => ({
    calendar: settings.calendar,
    services: settings.appointmentServices ?? [],
  }),
  channels: (settings) => ({
    chat: {
      ...settings.channels.chat,
      maxCatalogCards: settings.channels.chat.maxCatalogCards ?? 3,
    },
    email: settings.channels.email,
  }),
};

function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonical);
  if (value === null || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.entries(value)
      .filter(([, entry]) => entry !== undefined)
      .sort(([first], [second]) => (first < second ? -1 : 1))
      .map(([key, entry]) => [key, canonical(entry)])
  );
}

export function changedSections(
  live: Settings,
  draft: Settings
): SettingsSection[] {
  return SETTINGS_SECTIONS.filter(
    (section) =>
      JSON.stringify(canonical(sections[section](live))) !==
      JSON.stringify(canonical(sections[section](draft)))
  );
}

export function modelFacts(
  settings: Settings,
  copy: Copy
): Array<{ label: string; value: string }> {
  const chosen = model(settings);
  return [
    {
      label: copy.responseModel,
      value:
        RESPONSE_MODELS.find((entry) => entry.model === chosen.model)
          ?.displayName ?? chosen.model,
    },
    {
      label: copy.reasoningEffort,
      value: copy[`reasoning_${chosen.reasoningEffort}`],
    },
    {
      label: copy.responseProcessingMode,
      value:
        chosen.processingMode === 'fast'
          ? copy.processingFast
          : copy.processingStandard,
    },
    { label: copy.responseOutputTokens, value: String(chosen.maxOutputTokens) },
  ];
}

export function modelChanges(
  live: Settings,
  draft: Settings,
  copy: Copy
): Array<{ label: string; from: string; to: string }> {
  const published = modelFacts(live, copy);
  return modelFacts(draft, copy).flatMap((fact, index) =>
    fact.value === published[index]!.value
      ? []
      : [{ label: fact.label, from: published[index]!.value, to: fact.value }]
  );
}
